import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useResetPassword } from '../hooks/use-reset-password';
import { Input, logoDark, logoLight } from '@react-apps/design-system';

const COPY = {
  'first-login': {
    title: 'Defina sua senha',
    subtitle:
      'Defina uma senha de acesso para começar a usar a plataforma Primum.',
    passwordLabel: 'Senha',
    buttonLabel: 'Definir senha',
    buttonLoadingLabel: 'Definindo…',
    successMessage: 'Senha definida! Você já pode entrar na plataforma.',
  },
  reset: {
    title: 'Redefina sua senha',
    subtitle: 'Escolha uma nova senha de acesso à sua conta Primum.',
    passwordLabel: 'Nova senha',
    buttonLabel: 'Redefinir senha',
    buttonLoadingLabel: 'Redefinindo…',
    successMessage: 'Senha redefinida! Você já pode entrar na plataforma.',
  },
} as const;

interface PasswordResetCardProps {
  mode: 'first-login' | 'reset';
  token: string | null;
}

export function PasswordResetCard({ mode, token }: PasswordResetCardProps) {
  const { resetPassword, isLoading, error, success } = useResetPassword();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  // Tira o token da barra de endereços e do histórico assim que a página monta
  // (mitigação obrigatória do token em query param — admin-user-registration §4.3).
  // O `token` já foi capturado pela página-pai antes de renderizar, então limpar
  // a URL aqui é seguro.
  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const copy = COPY[mode];

  const handleSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!token) return;
    if (password.length < 8) {
      setFormError('A senha precisa ter no mínimo 8 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('As senhas não coincidem.');
      return;
    }
    setFormError(null);
    void resetPassword({ token, password });
  };

  return (
    <div className="bg-(--color-bg-secondary) flex min-h-screen flex-col items-center justify-center px-4">
      <div className="flex w-full max-w-sm flex-1 flex-col items-center justify-center">
        {/* Logo */}
        <div className="mb-8">
          <img
            src={isDark ? logoDark : logoLight}
            alt="Primum"
            className="h-10 w-auto"
          />
        </div>

        {/* Heading */}
        <div className="mb-8 text-center">
          <h1 className="text-(--color-text-primary) text-2xl font-bold">
            {copy.title}
          </h1>
          <p className="text-(--color-text-secondary) mt-2 text-sm">
            {copy.subtitle}
          </p>
        </div>

        {/* Card */}
        <div className="border-(--color-border) bg-(--color-bg-primary) w-full rounded-2xl border p-8 shadow-sm">
          {!token ? (
            <div className="space-y-6">
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-center dark:border-red-900 dark:bg-red-950">
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  Link inválido ou expirado. Peça um novo.
                </p>
              </div>
              <Link
                to="/sign-in"
                className="bg-(--color-accent) flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
              >
                Voltar para o login
              </Link>
            </div>
          ) : success ? (
            <div className="space-y-6">
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-6 text-center dark:border-green-900 dark:bg-green-950">
                <svg
                  className="mx-auto mb-3 h-8 w-8 text-green-600 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                  />
                </svg>
                <p className="text-sm font-medium text-green-700 dark:text-green-400">
                  {copy.successMessage}
                </p>
              </div>
              <Link
                to="/sign-in"
                className="bg-(--color-accent) flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
              >
                Ir para o login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                id="password"
                label={copy.passwordLabel}
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />

              <Input
                id="confirmPassword"
                label="Confirmar senha"
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
              />

              {(formError ?? error) && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
                  {formError ?? 'Link inválido ou expirado. Peça um novo.'}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !password || !confirmPassword}
                className="bg-(--color-accent) w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? copy.buttonLoadingLabel : copy.buttonLabel}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6">
        <p className="text-(--color-text-secondary) text-xs">
          © 2026 Grupo Primum. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
}
